// Modelos de Glubi: los que el equipo conecta con la clave de Glubi y da a cada negocio en su gateway. El negocio los
// ve con el nombre «glubi/…» al lado de los suyos, los usa en sus agentes y sus llaves, y no los puede cambiar.
// Cada negocio que recibe un modelo trae su propio tope al mes: no hay uno por defecto, se decide negocio por negocio.
// La clave entra una vez y no vuelve a salir: se cifra en el gateway, que es el único que tiene la llave.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Cargando, Input, Modal, PageHead, Select, Tabla, TarjetaIndicador, Vacio } from '@glubi/ds';
import { apiGateway, mensajeDeError } from '../lib/api';
import { desde } from '../lib/formato';
import { useSesion } from '../lib/sesion';

type Tipo = 'openai' | 'anthropic' | 'compatible';
const TIPO: Record<Tipo, string> = { openai: 'OpenAI', anthropic: 'Anthropic', compatible: 'Compatible con OpenAI' };

// Puntos de partida; la dirección se puede cambiar antes de guardar.
const PLANTILLAS: { titulo: string; tipo: Tipo; urlBase: string }[] = [
  { titulo: 'OpenAI', tipo: 'openai', urlBase: 'https://api.openai.com/v1' },
  { titulo: 'Anthropic', tipo: 'anthropic', urlBase: 'https://api.anthropic.com' },
  { titulo: 'Google Gemini', tipo: 'compatible', urlBase: 'https://generativelanguage.googleapis.com/v1beta/openai' },
  { titulo: 'OpenRouter', tipo: 'compatible', urlBase: 'https://openrouter.ai/api/v1' },
  { titulo: 'Otro compatible con OpenAI', tipo: 'compatible', urlBase: 'https://' },
];

interface FormProveedor { id: string | null; nombre: string; tipo: Tipo; urlBase: string; clave: string; conClave: boolean; estado: 'activo' | 'pausado' }
interface Destino { proveedorId: string; modeloProveedor: string; precioEntrada: string; precioSalida: string }
interface FormModelo { id: string | null; nombre: string; descripcion: string; estado: 'activo' | 'pausado'; destinos: Destino[] }
interface FormAsignacion { modeloId: string; modelo: string; cuentaId: string; nueva: boolean; tope: string; estado: 'activo' | 'pausado' }

export function usd(v: unknown): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return 'US$0';
  if (n < 0.01) return `US$${n.toLocaleString('es-CO', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
  return `US$${n.toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const decimal = (v: string) => v.replace(',', '.');
const precioMalo = (v: string) => v.trim() !== '' && !(Number(v) >= 0);

export function Modelos() {
  const { aviso } = useSesion();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['glubi-modelos'], queryFn: () => apiGateway<any>('/api/glubi/modelos') });
  const [elegir, setElegir] = useState(false);
  const [prov, setProv] = useState<FormProveedor | null>(null);
  const [mod, setMod] = useState<FormModelo | null>(null);
  const [asig, setAsig] = useState<FormAsignacion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [disponibles, setDisponibles] = useState<Record<string, string[]>>({});
  const recargar = () => void qc.invalidateQueries({ queryKey: ['glubi-modelos'] });

  const guardarProveedor = useMutation({
    mutationFn: (f: FormProveedor) => apiGateway(f.id ? `/api/glubi/proveedores/${f.id}` : '/api/glubi/proveedores', {
      cuerpo: { nombre: f.nombre, tipo: f.tipo, urlBase: f.urlBase, estado: f.estado, ...(f.clave ? { clave: f.clave } : {}) },
    }),
    onSuccess: () => { recargar(); aviso('Proveedor guardado'); setProv(null); },
    onError: (e) => setError(mensajeDeError(e)),
  });
  const borrarProveedor = useMutation({
    mutationFn: (id: string) => apiGateway(`/api/glubi/proveedores/${id}/borrar`, { cuerpo: {} }),
    onSuccess: () => { recargar(); aviso('Proveedor quitado'); },
    onError: (e) => aviso(mensajeDeError(e)),
  });
  const probar = useMutation({
    mutationFn: (id: string) => apiGateway<{ ok: boolean; modelos?: string[]; error?: string }>(`/api/glubi/proveedores/${id}/modelos`, { cuerpo: {} }),
    onSuccess: (r, id) => {
      if (r.ok) { setDisponibles((d) => ({ ...d, [id]: r.modelos ?? [] })); aviso(`Conexión bien: ${r.modelos?.length ?? 0} modelos disponibles`); }
      else aviso(r.error ?? 'No respondió');
    },
    onError: (e) => aviso(mensajeDeError(e)),
  });
  const guardarModelo = useMutation({
    mutationFn: (f: FormModelo) => apiGateway(f.id ? `/api/glubi/modelos/${f.id}` : '/api/glubi/modelos', {
      cuerpo: {
        nombre: `glubi/${f.nombre}`, descripcion: f.descripcion || null, estado: f.estado,
        destinos: f.destinos.map((d) => ({ proveedorId: d.proveedorId, modeloProveedor: d.modeloProveedor, precioEntrada: Number(d.precioEntrada || 0), precioSalida: Number(d.precioSalida || 0) })),
      },
    }),
    onSuccess: () => { recargar(); aviso('Modelo guardado'); setMod(null); },
    onError: (e) => setError(mensajeDeError(e)),
  });
  const borrarModelo = useMutation({
    mutationFn: (id: string) => apiGateway(`/api/glubi/modelos/${id}/borrar`, { cuerpo: {} }),
    onSuccess: () => { recargar(); aviso('Modelo quitado de todos los negocios'); },
    onError: (e) => aviso(mensajeDeError(e)),
  });
  const asignar = useMutation({
    mutationFn: (f: FormAsignacion) => apiGateway(`/api/glubi/modelos/${f.modeloId}/cuentas`, { cuerpo: { cuentaId: f.cuentaId, topeMensual: Number(f.tope), estado: f.estado } }),
    onSuccess: () => { recargar(); aviso('Listo. Queda en la bitácora del negocio.'); setAsig(null); },
    onError: (e) => setError(mensajeDeError(e)),
  });
  const quitar = useMutation({
    mutationFn: (id: string) => apiGateway(`/api/glubi/asignaciones/${id}/quitar`, { cuerpo: {} }),
    onSuccess: () => { recargar(); aviso('Quitado. Queda en la bitácora del negocio.'); },
    onError: (e) => aviso(mensajeDeError(e)),
  });

  if (q.isLoading) return <Cargando />;
  if (q.error || !q.data) return <div className="page"><PageHead title="Modelos de Glubi" /><div className="error-caja">{mensajeDeError(q.error)}</div></div>;
  const { proveedores, modelos, cuentas } = q.data as { proveedores: any[]; modelos: any[]; cuentas: { id: string; nombre: string }[] };
  const asignaciones = modelos.flatMap((m) => m.asignaciones);
  const gastoMes = modelos.reduce((n, m) => n + m.gastoMes, 0);
  const alTope = asignaciones.filter((a) => a.gastoMes >= a.topeMensual).length;

  const destinoVacio = (): Destino => ({ proveedorId: proveedores[0]?.id ?? '', modeloProveedor: '', precioEntrada: '', precioSalida: '' });
  const cambiarDestino = (i: number, cambio: Partial<Destino>) => setMod((m) => m && { ...m, destinos: m.destinos.map((d, j) => (j === i ? { ...d, ...cambio } : d)) });
  const mover = (i: number) => setMod((m) => {
    if (!m) return m;
    const d = [...m.destinos];
    const [x] = d.splice(i, 1);
    d.splice(i - 1, 0, x!);
    return { ...m, destinos: d };
  });

  return (
    <div className="page">
      <PageHead title="Modelos de Glubi" subtitle="Modelos que Glubi conecta con su propia clave y da a cada negocio en su gateway, con un tope al mes para cada uno. El negocio los usa en sus agentes y sus llaves, y no los puede cambiar.">
        <Button variant="secondary" icon="plus" onClick={() => { setError(null); setElegir(true); }}>Conectar proveedor</Button>
        <Button icon="plus" disabled={!proveedores.length} onClick={() => { setError(null); setMod({ id: null, nombre: '', descripcion: '', estado: 'activo', destinos: [destinoVacio()] }); }}>Nuevo modelo</Button>
      </PageHead>

      <div className="kpis">
        <TarjetaIndicador label="Gastado este mes" value={usd(gastoMes)} trend="Lo que les cuesta a los negocios, al precio de cada destino" />
        <TarjetaIndicador label="Modelos" value={modelos.filter((m) => m.estado === 'activo').length} trend={`${proveedores.length} proveedores conectados`} />
        <TarjetaIndicador label="Negocios con modelos" value={new Set(asignaciones.map((a) => a.cuentaId)).size} trend={`${asignaciones.length} asignaciones`} />
        <TarjetaIndicador label="En su tope" value={alTope} tone={alTope > 0 ? 'pending' : 'ok'} trend="Responden 429 hasta el mes siguiente o hasta subirles el tope" />
      </div>

      {!modelos.length ? (
        <Vacio titulo="Todavía no hay modelos de Glubi">{proveedores.length ? 'Crear un modelo con el nombre que van a ver los negocios, por ejemplo «glubi/luna».' : 'Primero conectar un proveedor con la clave de Glubi.'}</Vacio>
      ) : modelos.map((m) => (
        <section key={m.id} className="card" style={{ marginBottom: 16, padding: 16 }}>
          <div className="entre" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div>
              <code style={{ fontSize: 15 }}>{m.nombre}</code> {m.estado === 'pausado' && <Badge state="pending">Pausado</Badge>}
              {m.descripcion && <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>{m.descripcion}</div>}
              <div style={{ fontSize: 13, marginTop: 6 }}>
                {m.destinos.map((d: any, i: number) => (
                  <span key={i}>{i > 0 && <span className="muted"> · si falla → </span>}{d.proveedor} <code>{d.modeloProveedor}</code> <span className="muted">({usd(d.precioEntrada)} / {usd(d.precioSalida)} por M)</span>{d.estadoProveedor === 'pausado' && <> <Badge state="pending">proveedor pausado</Badge></>}</span>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button size="sm" icon="users" onClick={() => { setError(null); setAsig({ modeloId: m.id, modelo: m.nombre, cuentaId: '', nueva: true, tope: '', estado: 'activo' }); }}>Dar a un negocio</Button>
              <Button size="sm" variant="secondary" icon="pencil" onClick={() => { setError(null); setMod({
                id: m.id, nombre: m.nombre.replace(/^glubi\//, ''), descripcion: m.descripcion ?? '', estado: m.estado,
                destinos: m.destinos.map((d: any) => ({ proveedorId: d.proveedorId, modeloProveedor: d.modeloProveedor, precioEntrada: String(d.precioEntrada ?? ''), precioSalida: String(d.precioSalida ?? '') })),
              }); }}>Cambiar</Button>
              <Button size="sm" variant="ghost" icon="trash" onClick={() => { if (confirm(`¿Quitar «${m.nombre}» de Glubi? Sale de ${m.asignaciones.length} negocios y sus agentes dejan de poder usarlo.`)) borrarModelo.mutate(m.id); }}>Quitar</Button>
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            {!m.asignaciones.length ? <div className="muted" style={{ fontSize: 13 }}>Ningún negocio lo tiene todavía.</div> : (
              <Tabla rowKey="id" rows={m.asignaciones}
                columns={[
                  { key: 'negocio', label: 'Negocio' },
                  { key: 'gasto', label: 'Este mes', render: (a: any) => <GastoContraTope gasto={a.gastoMes} tope={a.topeMensual} /> },
                  { key: 'llamadas', label: 'Llamadas', numeric: true, render: (a: any) => <span style={{ fontSize: 13 }}>{a.llamadasMes}{a.rechazadasMes > 0 && <span className="muted"> · {a.rechazadasMes} rechazadas</span>}<br /><span className="muted">{a.ultimaLlamada ? `última ${desde(a.ultimaLlamada).toLowerCase()}` : 'sin uso'}</span></span> },
                  { key: 'estado', label: 'Estado', render: (a: any) => <Badge state={a.estado === 'activo' ? 'ok' : 'pending'}>{a.estado === 'activo' ? 'Activo' : 'Pausado'}</Badge> },
                  { key: 'acciones', label: '', render: (a: any) => (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button size="sm" variant="secondary" onClick={() => { setError(null); setAsig({ modeloId: m.id, modelo: m.nombre, cuentaId: a.cuentaId, nueva: false, tope: String(a.topeMensual), estado: a.estado }); }}>Cambiar tope</Button>
                      <Button size="sm" variant="ghost" icon="trash" onClick={() => { if (confirm(`¿Quitarle «${m.nombre}» a ${a.negocio}?`)) quitar.mutate(a.id); }}>Quitar</Button>
                    </div>
                  ) },
                ]} />
            )}
          </div>
        </section>
      ))}

      <div className="eyebrow" style={{ margin: '22px 0 8px' }}>Proveedores de Glubi</div>
      {!proveedores.length ? (
        <Vacio titulo="Ningún proveedor todavía" accion={<Button icon="plus" onClick={() => setElegir(true)}>Conectar proveedor</Button>}>
          OpenAI, Anthropic, Gemini, OpenRouter o cualquiera compatible con OpenAI, con la clave de Glubi.
        </Vacio>
      ) : (
        <Tabla rowKey="id" rows={proveedores}
          columns={[
            { key: 'nombre', label: 'Proveedor', render: (p: any) => <b style={{ fontWeight: 500 }}>{p.nombre}</b> },
            { key: 'tipo', label: 'Tipo', render: (p: any) => TIPO[p.tipo as Tipo] },
            { key: 'url_base', label: 'Dirección', render: (p: any) => <code style={{ fontSize: 12 }}>{p.url_base}</code> },
            { key: 'pista', label: 'Clave', render: (p: any) => (p.con_clave ? <code>{p.pista}</code> : <span className="muted">Sin clave</span>) },
            { key: 'modelos', label: 'Modelos', numeric: true },
            { key: 'estado', label: 'Estado', render: (p: any) => <Badge state={p.estado === 'activo' ? 'ok' : 'pending'}>{p.estado === 'activo' ? 'Activo' : 'Pausado'}</Badge> },
            { key: 'acciones', label: '', render: (p: any) => (
              <div style={{ display: 'flex', gap: 6 }}>
                <Button size="sm" variant="ghost" icon="signal" disabled={probar.isPending} onClick={() => probar.mutate(p.id)}>Probar</Button>
                <Button size="sm" variant="secondary" icon="pencil" onClick={() => { setError(null); setProv({ id: p.id, nombre: p.nombre, tipo: p.tipo, urlBase: p.url_base, clave: '', conClave: p.con_clave, estado: p.estado }); }}>Cambiar</Button>
                <Button size="sm" variant="ghost" icon="trash" disabled={p.modelos > 0} onClick={() => { if (confirm(`¿Quitar «${p.nombre}»?`)) borrarProveedor.mutate(p.id); }}>Quitar</Button>
              </div>
            ) },
          ]} />
      )}

      {elegir && (
        <Modal title="Conectar un proveedor" subtitle="De dónde salen los modelos de Glubi. La dirección y la clave se pueden cambiar después." onClose={() => setElegir(false)} width={620}>
          <div className="stack">
            {PLANTILLAS.map((p) => (
              <Button key={p.titulo} variant="secondary" onClick={() => {
                setElegir(false);
                setProv({ id: null, nombre: p.titulo.startsWith('Otro') ? '' : `${p.titulo} de Glubi`, tipo: p.tipo, urlBase: p.urlBase, clave: '', conClave: false, estado: 'activo' });
              }}>{p.titulo}</Button>
            ))}
          </div>
        </Modal>
      )}

      {prov && (
        <Modal title={prov.id ? `Cambiar «${prov.nombre}»` : 'Conectar proveedor'} onClose={() => setProv(null)} width={620}
          footer={<>
            <Button variant="ghost" onClick={() => setProv(null)}>Cancelar</Button>
            <Button disabled={guardarProveedor.isPending || prov.nombre.trim().length < 2 || !prov.urlBase.startsWith('http')} onClick={() => { setError(null); guardarProveedor.mutate(prov); }}>Guardar</Button>
          </>}>
          <div className="stack">
            <Input label="Nombre" value={prov.nombre} onChange={(e) => setProv({ ...prov, nombre: e.target.value })} helper="Solo lo ve el equipo" />
            <Select label="Forma de la API" value={prov.tipo} onChange={(e) => setProv({ ...prov, tipo: e.target.value as Tipo })}
              options={(Object.keys(TIPO) as Tipo[]).map((t) => ({ value: t, label: TIPO[t] }))} />
            <Input label="Dirección base" value={prov.urlBase} onChange={(e) => setProv({ ...prov, urlBase: e.target.value })} />
            <Input label={prov.conClave ? 'Clave nueva (opcional)' : 'Clave de la API'} type="password" value={prov.clave} onChange={(e) => setProv({ ...prov, clave: e.target.value })}
              helper={prov.conClave ? 'Vacía deja la que hay. Se guarda cifrada y no se vuelve a mostrar' : 'Se guarda cifrada en el gateway y no se vuelve a mostrar, ni al equipo ni a los negocios'} />
            {prov.id && <Select label="Estado" value={prov.estado} onChange={(e) => setProv({ ...prov, estado: e.target.value as 'activo' | 'pausado' })} options={[{ value: 'activo', label: 'Activo' }, { value: 'pausado', label: 'Pausado: sus modelos saltan al respaldo' }]} />}
            {error && <div className="error-caja">{error}</div>}
          </div>
        </Modal>
      )}

      {mod && (
        <Modal title={mod.id ? `Cambiar «glubi/${mod.nombre}»` : 'Nuevo modelo de Glubi'} subtitle="Los negocios lo ven y lo piden con este nombre. Los precios son en dólares por millón de tokens: con ellos se mide el gasto contra el tope de cada negocio." onClose={() => setMod(null)} width={760}
          footer={<>
            <Button variant="ghost" onClick={() => setMod(null)}>Cancelar</Button>
            <Button disabled={guardarModelo.isPending || !mod.nombre.trim() || mod.destinos.some((d) => !d.proveedorId || !d.modeloProveedor.trim() || precioMalo(d.precioEntrada) || precioMalo(d.precioSalida))} onClick={() => { setError(null); guardarModelo.mutate(mod); }}>Guardar</Button>
          </>}>
          <div className="stack">
            <Input label="Nombre para los negocios" value={mod.nombre} onChange={(e) => setMod({ ...mod, nombre: e.target.value.toLowerCase().replace(/^glubi\//, '') })} placeholder="luna"
              helper={`El negocio lo ve y lo pide como «glubi/${mod.nombre || '…'}». Minúsculas, números y . _ -`} />
            <Input label="Para qué es" value={mod.descripcion} onChange={(e) => setMod({ ...mod, descripcion: e.target.value })} helper="Lo lee el negocio en su consola" />
            <span className="etiqueta">Destinos, en orden</span>
            {mod.destinos.map((d, i) => (
              <div key={i} className="card" style={{ padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Badge state={i === 0 ? 'ok' : 'neutral'}>{i === 0 ? 'Principal' : `Respaldo ${i}`}</Badge>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => mover(i)}>Subir</Button>
                    <Button size="sm" variant="ghost" icon="trash" disabled={mod.destinos.length === 1} onClick={() => setMod({ ...mod, destinos: mod.destinos.filter((_, j) => j !== i) })}>Quitar</Button>
                  </div>
                </div>
                <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                  <Select label="Proveedor" value={d.proveedorId} onChange={(e) => cambiarDestino(i, { proveedorId: e.target.value })}
                    options={proveedores.map((p) => ({ value: p.id as string, label: p.nombre as string }))} />
                  <div>
                    <Input label="Modelo del proveedor" value={d.modeloProveedor} onChange={(e) => cambiarDestino(i, { modeloProveedor: e.target.value })}
                      helper={disponibles[d.proveedorId] ? `${disponibles[d.proveedorId]!.length} disponibles; empezar a escribir` : 'Con «Probar» en el proveedor se ve la lista'} />
                    {disponibles[d.proveedorId] && d.modeloProveedor.length > 0 && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                        {disponibles[d.proveedorId]!.filter((x) => x.includes(d.modeloProveedor) && x !== d.modeloProveedor).slice(0, 6).map((x) => (
                          <Button key={x} size="sm" variant="ghost" onClick={() => cambiarDestino(i, { modeloProveedor: x })}>{x}</Button>
                        ))}
                      </div>
                    )}
                  </div>
                  <Input label="Precio de entrada" unit="US$ / M" inputMode="decimal" value={d.precioEntrada} onChange={(e) => cambiarDestino(i, { precioEntrada: decimal(e.target.value) })} error={precioMalo(d.precioEntrada) ? 'Un número, por ejemplo 0.25' : undefined} />
                  <Input label="Precio de salida" unit="US$ / M" inputMode="decimal" value={d.precioSalida} onChange={(e) => cambiarDestino(i, { precioSalida: decimal(e.target.value) })} error={precioMalo(d.precioSalida) ? 'Un número, por ejemplo 2' : undefined} />
                </div>
              </div>
            ))}
            {mod.destinos.length < 8 && <div><Button size="sm" variant="secondary" icon="plus" onClick={() => setMod({ ...mod, destinos: [...mod.destinos, destinoVacio()] })}>Agregar respaldo</Button></div>}
            {mod.id && <Select label="Estado" value={mod.estado} onChange={(e) => setMod({ ...mod, estado: e.target.value as 'activo' | 'pausado' })} options={[{ value: 'activo', label: 'Activo' }, { value: 'pausado', label: 'Pausado: ningún negocio lo puede usar' }]} />}
            {error && <div className="error-caja">{error}</div>}
          </div>
        </Modal>
      )}

      {asig && (
        <Modal title={asig.nueva ? `Dar «${asig.modelo}» a un negocio` : `Tope de «${asig.modelo}»`} subtitle="El tope es lo que este negocio puede gastar al mes en este modelo, en dólares. Al llegar, el modelo responde 429 hasta el mes siguiente." onClose={() => setAsig(null)}
          footer={<>
            <Button variant="ghost" onClick={() => setAsig(null)}>Cancelar</Button>
            <Button disabled={asignar.isPending || !asig.cuentaId || asig.tope.trim() === '' || !(Number(asig.tope) >= 0)} onClick={() => { setError(null); asignar.mutate(asig); }}>{asig.nueva ? 'Dar' : 'Guardar'}</Button>
          </>}>
          <div className="stack">
            {asig.nueva ? (
              <Select label="Negocio" value={asig.cuentaId} onChange={(e) => setAsig({ ...asig, cuentaId: e.target.value })}
                options={[{ value: '', label: 'Elegir' }, ...cuentas.map((c) => ({ value: c.id, label: c.nombre }))]} />
            ) : <div><span className="etiqueta">Negocio</span><div>{cuentas.find((c) => c.id === asig.cuentaId)?.nombre}</div></div>}
            <Input label="Tope al mes" unit="US$" inputMode="decimal" value={asig.tope} onChange={(e) => setAsig({ ...asig, tope: decimal(e.target.value) })} helper="Cero lo deja asignado pero sin uso" autoFocus />
            <Select label="Estado" value={asig.estado} onChange={(e) => setAsig({ ...asig, estado: e.target.value as 'activo' | 'pausado' })}
              options={[{ value: 'activo', label: 'Activo' }, { value: 'pausado', label: 'Pausado: el negocio lo ve pero no lo puede usar' }]} />
            <div className="aviso-caja">Queda en la bitácora del negocio, con el nombre de quien lo hizo.</div>
            {error && <div className="error-caja">{error}</div>}
          </div>
        </Modal>
      )}
    </div>
  );
}

function GastoContraTope({ gasto, tope }: { gasto: number; tope: number }) {
  const pct = tope > 0 ? Math.min(100, (gasto / tope) * 100) : 100;
  const lleno = gasto >= tope;
  return (
    <div style={{ minWidth: 160 }}>
      <div className="num" style={{ fontSize: 13 }}>{usd(gasto)} <span className="muted">de {usd(tope)}</span></div>
      <div style={{ height: 4, borderRadius: 2, background: 'var(--surface-sunken)', marginTop: 4 }}>
        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 2, background: lleno ? 'var(--state-error)' : 'var(--amber)' }} />
      </div>
    </div>
  );
}
